import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Ed, Reveal } from "../components/Editorial";
import { fmtRange } from "../lib/time";
import { PasswordField } from "../components/PasswordField";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

// Anillo de progreso hacia la clase de regalo. Un número suelto a cero no
// dice nada; el anillo enseña cuánto falta y da ganas de cerrarlo.
const AnilloProgreso = ({ hechas, meta, tamano = 168 }) => {
  const r = tamano / 2 - 9;
  const circ = 2 * Math.PI * r;
  const avance = meta > 0 ? Math.min(hechas / meta, 1) : 0;
  return (
    <svg width={tamano} height={tamano} viewBox={`0 0 ${tamano} ${tamano}`} className="shrink-0">
      <circle cx={tamano / 2} cy={tamano / 2} r={r} fill="none" stroke="#232323" strokeWidth="2" />
      <circle
        cx={tamano / 2}
        cy={tamano / 2}
        r={r}
        fill="none"
        stroke="#C5A059"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={`${circ * avance} ${circ}`}
        transform={`rotate(-90 ${tamano / 2} ${tamano / 2})`}
        style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.22,1,0.36,1)" }}
      />
      <text x="50%" y="47%" textAnchor="middle" className="fill-white" style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: tamano * 0.3 }}>
        {hechas}
      </text>
      <text x="50%" y="63%" textAnchor="middle" fill="#71717a" style={{ fontFamily: "Inter,sans-serif", fontSize: 10, letterSpacing: 2 }}>
        / {meta}
      </text>
    </svg>
  );
};

function AuthForms() {
  const { t } = useLang();
  const a = t.account;
  const { login, register, adoptSession } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [vista, setVista] = useState("auth");   // auth · olvido · enviado · nueva
  const [aviso, setAviso] = useState("");
  const { lang } = useLang();

  // Si el cliente llega desde el enlace del correo, se le pide la nueva clave.
  const tokenReset = new URLSearchParams(window.location.search).get("reset");
  useEffect(() => {
    if (tokenReset) setVista("nueva");
  }, [tokenReset]);

  const pedirEnlace = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot`, { email, lang });
    } catch (err) {
      /* la respuesta es siempre la misma, exista o no la cuenta */
    } finally {
      setBusy(false);
      setVista("enviado");
    }
  };

  const guardarNueva = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/reset`, { token: tokenReset, password });
      window.history.replaceState({}, "", "/account");
      adoptSession(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || a.genericError);
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register({ name, email, password, phone });
      }
    } catch (err) {
      setError(err.response?.data?.detail || a.genericError);
    } finally {
      setBusy(false);
    }
  };

  const Marco = ({ titulo, children }) => (
    <div className="max-w-md mx-auto" data-testid="auth-forms">
      <h2 className="font-serif text-2xl text-white">{titulo}</h2>
      {children}
    </div>
  );

  // Paso 1: pedir el enlace
  if (vista === "olvido") {
    return (
      <Marco titulo={a.forgotTitle}>
        <p className="mt-4 text-sm text-zinc-500 font-light leading-relaxed">{a.forgotIntro}</p>
        <form onSubmit={pedirEnlace} className="mt-7 space-y-3" noValidate>
          <input data-testid="forgot-email" type="email" className="input-dark" placeholder={a.email}
                 value={email} onChange={(e) => setEmail(e.target.value)} />
          <button data-testid="forgot-submit" type="submit" disabled={busy || !email.includes("@")}
                  className="btn-gold w-full !py-4 disabled:opacity-40">
            {busy ? "…" : a.forgotSend}
          </button>
        </form>
        <button onClick={() => setVista("auth")}
                className="mt-6 w-full text-center text-[0.7rem] text-zinc-500 hover:text-[#C5A059] transition-colors duration-300">
          {a.backToSignIn}
        </button>
      </Marco>
    );
  }

  // Paso 2: confirmación (nunca decimos si el correo existe o no)
  if (vista === "enviado") {
    return (
      <Marco titulo={a.forgotSentTitle}>
        <p className="mt-4 text-sm text-zinc-400 font-light leading-relaxed">{a.forgotSentBody}</p>
        <p className="mt-3 text-[0.7rem] text-zinc-600 leading-relaxed">{a.forgotSentSpam}</p>
        <button onClick={() => setVista("auth")} className="btn-gold w-full !py-4 mt-8">{a.backToSignIn}</button>
      </Marco>
    );
  }

  // Paso 3: contraseña nueva, llegando desde el enlace del correo
  if (vista === "nueva") {
    return (
      <Marco titulo={a.resetTitle}>
        <p className="mt-4 text-sm text-zinc-500 font-light leading-relaxed">{a.resetIntro}</p>
        <form onSubmit={guardarNueva} className="mt-7 space-y-3" noValidate>
          <PasswordField testId="reset-password" placeholder={a.newPassword} value={password}
                         autoComplete="new-password" mostrarLabel={a.showPassword} ocultarLabel={a.hidePassword}
                         onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="reset-error" className="text-xs text-red-400">{error}</p>}
          <button data-testid="reset-submit" type="submit" disabled={busy || password.length < 6}
                  className="btn-gold w-full !py-4 disabled:opacity-40">
            {busy ? "…" : a.resetSave}
          </button>
        </form>
        <p className="mt-5 text-[0.7rem] text-zinc-600">{a.resetMin}</p>
      </Marco>
    );
  }

  return (
    <div className="max-w-md mx-auto" data-testid="auth-forms">
      <div className="flex gap-8 border-b border-white/10">
        {["login", "register"].map((m) => (
          <button
            key={m}
            data-testid={`auth-tab-${m}`}
            onClick={() => {
              setMode(m);
              setError("");
            }}
            className={`pb-3 text-[0.7rem] uppercase tracking-[0.25em] transition-colors duration-300 ${
              mode === m ? "text-[#C5A059] border-b border-[#C5A059] -mb-px" : "text-zinc-500 hover:text-white"
            }`}
          >
            {m === "login" ? a.signIn : a.createAccount}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-3" noValidate>
        {mode === "register" && (
          <input data-testid="auth-name" className="input-dark" placeholder={a.name} value={name} onChange={(e) => setName(e.target.value)} />
        )}
        <input data-testid="auth-email" type="email" className="input-dark" placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
        {mode === "register" && (
          <input data-testid="auth-phone" className="input-dark" placeholder={a.phoneOptional} value={phone} onChange={(e) => setPhone(e.target.value)} />
        )}
        <PasswordField
          testId="auth-password"
          placeholder={a.password}
          value={password}
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          mostrarLabel={a.showPassword}
          ocultarLabel={a.hidePassword}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && <p data-testid="auth-error" className="text-xs text-red-400">{error}</p>}

        <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-gold w-full !py-4">
          {busy ? "…" : mode === "login" ? a.signIn : a.createAccount}
        </button>
      </form>

      {mode === "login" && (
        <button
          data-testid="forgot-link"
          onClick={() => { setError(""); setVista("olvido"); }}
          className="mt-5 w-full text-center text-[0.7rem] text-zinc-500 hover:text-[#C5A059] transition-colors duration-300"
        >
          {a.forgotLink}
        </button>
      )}
      <p className="mt-6 text-[0.7rem] text-zinc-600 text-center">{a.privacyNote}</p>
    </div>
  );
}

function Dashboard() {
  const { lang, t } = useLang();
  const a = t.account;
  const { account, logout, purchaseMembership, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [busyPkg, setBusyPkg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const m = account.membership;
  const memActive = m && m.active;

  const buy = async (id) => {
    setBusyPkg(id);
    try {
      await purchaseMembership(id);
    } catch (e) {
      /* no-op */
    } finally {
      setBusyPkg(null);
    }
  };

  const first = (account.profile.name || "").split(" ")[0];

  return (
    <div data-testid="account-dashboard">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="overline-gold font-sans">{a.welcome}</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl text-white leading-none">{first || account.profile.email}</h1>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="text-[0.7rem] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#C5A059] transition-colors duration-300">
          {a.signOut}
        </button>
      </div>
      <div className="mt-8 h-px bg-gradient-to-r from-[#C5A059] via-[#C5A059]/25 to-transparent" />

      <div className="mt-12 grid gap-6 lg:grid-cols-[1.65fr_1fr] items-start">
        {/* Membresía */}
        <div className="border border-[#232323] bg-[#0C0C0C] p-8 md:p-10" data-testid="membership-card">
          <p className="overline-gold font-sans">{a.membership}</p>
          {memActive ? (
            <div className="mt-7">
              <p className="font-serif text-4xl text-white">{m.name[lang]}</p>
              <div className="mt-8 grid grid-cols-2 gap-px bg-[#232323] border border-[#232323]">
                <div className="bg-[#0C0C0C] px-6 py-5">
                  <p className="font-serif text-3xl text-[#C5A059] leading-none">
                    {m.unlimited ? "∞" : m.classes_remaining}
                  </p>
                  <p className="mt-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500">
                    {m.unlimited ? a.unlimited : a.classesLeft}
                  </p>
                </div>
                <div className="bg-[#0C0C0C] px-6 py-5">
                  <p className="font-serif text-3xl text-white leading-none">{fmtDate(m.expires_at)}</p>
                  <p className="mt-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500">{a.validUntil}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <p className="text-sm text-zinc-400 font-light leading-relaxed">{a.noMembership}</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="buy-membership">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`buy-${p.id}`}
                    onClick={() => buy(p.id)}
                    disabled={busyPkg === p.id}
                    className="group relative border border-[#232323] hover:border-[#C5A059] transition-colors duration-500 px-6 py-7 text-left disabled:opacity-40"
                  >
                    <span className="block font-serif text-2xl text-white">{p.name[lang]}</span>
                    <span className="block mt-4 font-serif text-4xl text-white leading-none">
                      <span className="align-top text-lg text-[#C5A059]">$</span>{p.price}
                    </span>
                    <span className="block mt-4 text-xs text-zinc-500 font-light leading-relaxed">{p.note[lang]}</span>
                    <span className="mt-5 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] opacity-60 group-hover:opacity-100 transition-opacity duration-500">
                      {busyPkg === p.id ? "…" : a.choose} <span aria-hidden>→</span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-6 text-[0.7rem] text-zinc-600">{a.payNote}</p>
            </div>
          )}
        </div>

        {/* Recompensa */}
        <div className="border border-[#232323] bg-[#0C0C0C] p-8 md:p-10 text-center" data-testid="reward-card">
          <p className="overline-gold font-sans text-left">{a.reward}</p>

          <div className="mt-8 flex justify-center">
            <AnilloProgreso
              hechas={Math.max(0, (account.reward_threshold || 20) - account.classes_to_next_reward)}
              meta={account.reward_threshold || 20}
            />
          </div>

          <p className="mt-7 text-xs text-zinc-500 leading-relaxed">
            {a.rewardProgress.replace("{n}", account.classes_to_next_reward)}
          </p>

          {account.free_credits > 0 && (
            <div className="mt-7 border border-[#C5A059]/40 bg-[#C5A059]/[0.06] px-5 py-4">
              <p className="font-serif text-3xl text-[#C5A059] leading-none">{account.free_credits}</p>
              <p className="mt-2 text-[0.6rem] uppercase tracking-[0.25em] text-[#C5A059]">{a.freeClasses}</p>
            </div>
          )}

          <p className="mt-7 pt-6 border-t border-white/10 text-[0.7rem] text-zinc-600">
            {a.totalBooked.replace("{n}", account.classes_booked)}
          </p>
        </div>
      </div>

      {/* Historial */}
      <div className="mt-16">
        <p className="overline-gold font-sans">{a.history}</p>
        {account.bookings.length === 0 ? (
          <div className="mt-8 border border-dashed border-[#232323] px-8 py-12 text-center">
            <p className="font-serif italic text-xl text-zinc-500">{a.noBookings}</p>
            <Link
              to="/book"
              className="mt-6 inline-block border border-[#C5A059]/50 px-7 py-3 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors duration-500"
            >
              {a.bookNow}
            </Link>
          </div>
        ) : (
          <div className="mt-8" data-testid="bookings-history">
            {account.bookings.map((bk) => (
              <div
                key={bk.receipt_number}
                className="group relative border-l border-[#232323] hover:border-[#C5A059] transition-colors duration-500 pl-7 pr-2 py-6"
              >
                <span className="absolute left-0 top-9 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-[#232323] group-hover:bg-[#C5A059] transition-colors duration-500" />
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div>
                    <p className="font-serif italic text-xl text-[#C5A059]">{bk.class_name ? bk.class_name[lang] : ""}</p>
                    <p className="mt-2 text-xs text-zinc-500 font-light">
                      {bk.class_date} · {fmtRange(bk.class_time, bk.class_time_end)} · {a.bikes}: {bk.bikes.join(", ")}
                    </p>
                    <p className="mt-1 text-[0.65rem] uppercase tracking-[0.2em] text-zinc-700">{bk.receipt_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl text-white leading-none">${Number(bk.total).toFixed(2)}</p>
                    <div className="mt-3 flex gap-2 justify-end flex-wrap">
                      {bk.membership_applied && <span className="text-[0.55rem] uppercase tracking-widest text-[#C5A059] border border-[#C5A059]/40 px-2 py-1">{a.byMembership}</span>}
                      {bk.free_class_applied && <span className="text-[0.55rem] uppercase tracking-widest text-[#C5A059] border border-[#C5A059]/40 px-2 py-1">{a.freeTag}</span>}
                    </div>
                    {/* Solo en las clases que aún se pueden mover sin cargo. */}
                    {bk.can_reschedule && bk.id && (
                      <Link
                        to={`/reserva/${bk.id}`}
                        data-testid={`reschedule-link-${bk.receipt_number}`}
                        className="mt-3 inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#C5A059] transition-colors duration-300"
                      >
                        <RefreshCw size={11} className="shrink-0" />
                        {a.reschedule}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Perfil */}
      <ProfileCard />
    </div>
  );
}

function ProfileCard() {
  const { t } = useLang();
  const a = t.account;
  const { account, updateProfile } = useAuth();
  const [name, setName] = useState(account.profile.name || "");
  const [phone, setPhone] = useState(account.profile.phone || "");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const dirty = useMemo(
    () => name !== (account.profile.name || "") || phone !== (account.profile.phone || ""),
    [name, phone, account.profile]
  );

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      await updateProfile({ name, phone });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-16 border border-[#232323] bg-[#0C0C0C] p-8 md:p-10 max-w-2xl" data-testid="profile-card">
      <p className="overline-gold font-sans">{a.profile}</p>
      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        <label className="block">
          <span className="block mb-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500">{a.name}</span>
          <input className="input-dark" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} />
        </label>
        <label className="block">
          <span className="block mb-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500">{a.phoneOptional}</span>
          <input className="input-dark" value={phone} onChange={(e) => { setPhone(e.target.value); setSaved(false); }} />
        </label>
        <label className="block sm:col-span-2">
          <span className="block mb-2 text-[0.6rem] uppercase tracking-[0.25em] text-zinc-500">{a.email}</span>
          <input className="input-dark bg-black/40 text-zinc-500 cursor-not-allowed" value={account.profile.email} disabled />
        </label>
      </div>
      <button data-testid="profile-save" onClick={save} disabled={!dirty || busy} className="btn-gold mt-8 !py-3 !px-10 disabled:opacity-30">
        {busy ? "…" : saved ? a.saved : a.save}
      </button>
    </div>
  );
}

function GuestLookup() {
  const { lang, t } = useLang();
  const a = t.account;
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.get(`${API}/customers/${encodeURIComponent(email.trim().toLowerCase())}/bookings`);
      setResult(r.data);
    } catch {
      setResult({ bookings: [] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-16 border-t border-white/10 pt-10" data-testid="guest-lookup">
      <p className="overline-gold text-center">{a.guestTitle}</p>
      <p className="mt-3 text-center text-sm text-zinc-500 font-light">{a.guestSub}</p>
      <form onSubmit={search} className="mt-6 flex gap-2">
        <input data-testid="guest-email" className="input-dark flex-1" type="email" placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="guest-search" className="btn-gold !px-5 !py-3 !text-[0.6rem]" disabled={busy}>
          {busy ? "…" : a.guestSearch}
        </button>
      </form>

      {result && (
        <div className="mt-6" data-testid="guest-results">
          {result.bookings.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center">{a.guestNone}</p>
          ) : (
            <>
              <p className="overline-gold mb-3">{a.guestFound}</p>
              <div className="divide-y divide-white/5">
                {result.bookings.map((bk) => (
                  <div key={bk.receipt_number} className="py-4 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-serif italic text-[#C5A059]">{bk.class_name ? bk.class_name[lang] : ""}</p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {bk.class_date} · {fmtRange(bk.class_time, bk.class_time_end)} · {a.bikes}: {bk.bikes.join(", ")}
                      </p>
                    </div>
                    <p className="text-white text-sm">${Number(bk.total).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { t } = useLang();
  const a = t.account;
  const { isAuth, loading } = useAuth();

  return (
    <main className="pt-32 pb-24 px-6 md:px-16 min-h-screen" data-testid="account-page">
      {loading ? (
        <p className="text-zinc-600 text-sm">…</p>
      ) : isAuth ? (
        <Reveal>
          <Dashboard />
        </Reveal>
      ) : (
        <Reveal>
          <div className="text-center mb-12">
            <p className="overline-gold font-sans">
              <Ed text={a.overline} goldItalic={false} />
            </p>
            <h1 className="mt-6 font-serif text-4xl md:text-6xl text-white">
              <Ed text={a.heading} />
            </h1>
          </div>
          <AuthForms />

          <GuestLookup />

          <div className="mt-24 md:mt-32 -mx-6 md:-mx-16 overflow-hidden select-none pointer-events-none" aria-hidden="true">
            <p className="font-serif text-[22vw] md:text-[17vw] leading-[0.8] text-white/90 whitespace-nowrap text-center translate-y-[0.08em]">
              APEX <em className="italic text-[#C5A059]/90">Ride</em>
            </p>
          </div>
        </Reveal>
      )}
    </main>
  );
}
